import { useEffect, useRef, useState } from 'react';

/**
 * Tiny scroll-parallax hook (no external deps).
 * Returns a `translateY(px)` value based on the element's distance from viewport center,
 * multiplied by `strength`.
 *
 *   const ref = useRef(null);
 *   const offset = useScrollParallax(ref, 0.05);
 *   <div ref={ref} style={{ transform: `translateY(${offset}px)` }} />
 */
export default function useScrollParallax(ref, strength = 0.05) {
  const [offset, setOffset] = useState(0);
  const ticking = useRef(false);

  useEffect(() => {
    const update = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = elementCenter - viewportCenter;
      setOffset(-distance * strength);
      ticking.current = false;
    };
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ref, strength]);

  return offset;
}
